import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { http, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import exifr from "exifr";
import {
  X, ArrowLeft, ArrowRight, MapPin, Camera, Sparkle, CheckCircle, UploadSimple,
  Warning,
} from "@phosphor-icons/react";

// AddObservation — mobile-first, multi-step submission for the NaturePulse
// Observation Network. Steps:
//   1. Category   — pick a leaf category (filtered by seedKind if provided)
//   2. Photos     — upload one or more images (base64 → /api/uploads/photo)
//   3. Location   — auto-detect + reverse-geocode into structured entities
//   4. Details    — title, description, date, time, species, severity, tags
//   5. Review     — trust labels + privacy + submit
//
// The form is idempotent — closing it discards the draft. Submissions go
// into moderation and never appear public until an admin approves.

const STEPS = ["Category", "Photos", "Location", "Details", "Review"];

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onerror = rej; r.onload = () => res(r.result); r.readAsDataURL(file);
  });
}
function absUrl(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

// Flatten category tree → leaves only, tagged with their root kind
function flattenLeaves(tree, ancestors = []) {
  const out = [];
  for (const node of tree || []) {
    const chain = [...ancestors, node];
    if (!node.children || node.children.length === 0) {
      out.push({
        slug: node.slug,
        label: node.label,
        emoji: chain.find((c) => c.emoji)?.emoji,
        kind: chain.find((c) => c.kind)?.kind,
        rootLabel: chain[0]?.label,
        breadcrumb: chain.map((c) => c.label).join(" › "),
        safety_note: chain.map((c) => c.safety_note).filter(Boolean).slice(-1)[0],
        anonymous_default: chain.some((c) => c.anonymous_default),
      });
    } else {
      out.push(...flattenLeaves(node.children, chain));
    }
  }
  return out;
}

export default function AddObservation({ seedKind, onClose, onSubmitted }) {
  const [step, setStep] = useState(0);
  const [tree, setTree] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [catQuery, setCatQuery] = useState("");

  // Step 2 — photos
  const [photos, setPhotos] = useState([]); // {file, previewUrl, url?, uploading, exif?}
  const fileRef = useRef(null);

  // Step 3 — location
  const [loc, setLoc] = useState(null); // {lat, lon, display_name, country, state, city, …}
  const [locBusy, setLocBusy] = useState(false);
  const [precision, setPrecision] = useState("exact");

  // Step 4 — details
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dateStr, setDateStr] = useState(new Date().toISOString().slice(0, 10));
  const [timeStr, setTimeStr] = useState(new Date().toTimeString().slice(0, 5));
  const [commonName, setCommonName] = useState("");
  const [scientificName, setScientificName] = useState("");
  const [severity, setSeverity] = useState("");
  const [passable, setPassable] = useState("");
  const [tagsStr, setTagsStr] = useState("");
  const [notes, setNotes] = useState("");

  // Step 5 — review
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [aiPayload, setAiPayload] = useState(null);

  // Load category tree
  useEffect(() => {
    http.get("/nature/categories").then((r) => setTree(r.data?.categories || [])).catch(() => setTree([]));
  }, []);

  const leaves = useMemo(() => {
    const all = flattenLeaves(tree);
    return seedKind ? all.filter((l) => l.kind === seedKind) : all;
  }, [tree, seedKind]);

  const filteredLeaves = useMemo(() => {
    const q = catQuery.trim().toLowerCase();
    if (!q) return leaves;
    return leaves.filter((l) => l.label.toLowerCase().includes(q) || l.breadcrumb.toLowerCase().includes(q));
  }, [leaves, catQuery]);

  // -------- Photo upload + EXIF extraction + AI analysis --------
  const [aiBusy, setAiBusy] = useState(false);
  const [aiApplied, setAiApplied] = useState(false);
  const [photoExif, setPhotoExif] = useState(null); // {lat,lon,ts}

  const pickPhotos = () => fileRef.current?.click();
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    e.target.value = "";
    if (!files.length) return;
    for (const f of files) {
      const previewUrl = URL.createObjectURL(f);
      const entry = { file: f, previewUrl, uploading: true };
      setPhotos((p) => [...p, entry]);

      // Attempt EXIF read in parallel — GPS + date
      exifr.parse(f, { gps: true, tiff: true, exif: true }).then((meta) => {
        const lat = meta?.latitude, lon = meta?.longitude;
        const ts = meta?.DateTimeOriginal || meta?.CreateDate || meta?.ModifyDate;
        if (lat != null && lon != null) {
          setPhotoExif((prev) => prev || { lat, lon, ts });
          // Auto-fill date/time from photo
          if (ts && typeof ts === "object") {
            const d = ts;
            setDateStr(d.toISOString().slice(0, 10));
            setTimeStr(d.toTimeString().slice(0, 5));
            setPhotos((p) => p.map((x) => x === entry ? { ...x, exif: { lat, lon, ts: d.toISOString() } } : x));
          }
        }
      }).catch(() => {});

      try {
        const b64 = await fileToBase64(f);
        const { data } = await http.post("/uploads/photo", { image_base64: b64, content_type: f.type || "image/jpeg" });
        setPhotos((p) => p.map((x) => x === entry ? { ...x, url: data.url, uploading: false, b64 } : x));

        // Auto-analyze the FIRST photo only (once) so the user isn't billed
        // for the whole batch.
        setPhotos((p) => {
          if (!aiApplied && p.filter((x) => x.url).length === 1) runAiAnalyze(b64);
          return p;
        });
      } catch (err) {
        setPhotos((p) => p.filter((x) => x !== entry));
        toast.error("Upload failed");
      }
    }
  };

  const runAiAnalyze = async (b64) => {
    if (aiBusy || aiApplied) return;
    setAiBusy(true);
    try {
      const { data } = await http.post("/nature/ai/analyze", {
        image_base64: b64,
        category_hint: seedKind || selectedCat?.kind || null,
        location_context: loc?.display_name || null,
        lat: loc?.lat, lon: loc?.lon,
      });
      // Pre-fill any empty fields; never overwrite what the user typed
      if (data?.title && !title) setTitle(String(data.title).slice(0, 180));
      if (data?.description && !description) setDescription(String(data.description).slice(0, 500));
      if (data?.common_name && !commonName) setCommonName(String(data.common_name).slice(0, 120));
      if (data?.scientific_name && !scientificName) setScientificName(String(data.scientific_name).slice(0, 120));
      if (Array.isArray(data?.tags) && !tagsStr) {
        setTagsStr(data.tags.slice(0, 8).map((t) => String(t).toLowerCase().trim()).filter(Boolean).join(", "));
      }
      // Store the AI payload so we can attach it (+ ai-suggested trust label) on submit
      setAiPayload({
        title: data?.title, description: data?.description,
        common_name: data?.common_name, scientific_name: data?.scientific_name,
        tags: data?.tags, confidence: data?.confidence,
        suggested_category_slug: data?.suggested_category_slug,
      });
      setAiApplied(true);
      toast.success(`AI Suggested · ${(data?.confidence != null ? Math.round(data.confidence * 100) + "%" : "ready")}`);
    } catch (e) {
      // Silently skip — user can still fill by hand
    } finally { setAiBusy(false); }
  };
  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  // -------- Location --------
  const detectLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await http.post("/nature/geo/lookup", { lat: pos.coords.latitude, lon: pos.coords.longitude });
        setLoc(data);
      } catch { toast.error("Could not resolve location"); }
      finally { setLocBusy(false); }
    }, () => { toast.error("Permission denied"); setLocBusy(false); }, { enableHighAccuracy: true, timeout: 15000 });
  };

  // Distance (km) between the photo's EXIF GPS and the currently-detected
  // device location. Used to decide whether to show the conflict picker.
  const photoVsLocKm = useMemo(() => {
    if (!photoExif?.lat || !loc?.lat) return null;
    const R = 6371, toR = (d) => (d * Math.PI) / 180;
    const dLat = toR(loc.lat - photoExif.lat), dLon = toR(loc.lon - photoExif.lon);
    const a = Math.sin(dLat/2)**2 + Math.cos(toR(photoExif.lat))*Math.cos(toR(loc.lat))*Math.sin(dLon/2)**2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
  }, [photoExif, loc]);
  const usePhotoLocation = async () => {
    if (!photoExif?.lat) return;
    setLocBusy(true);
    try {
      const { data } = await http.post("/nature/geo/lookup", { lat: photoExif.lat, lon: photoExif.lon });
      setLoc(data);
    } catch { toast.error("Could not resolve photo location"); }
    finally { setLocBusy(false); }
  };

  // -------- Submit --------
  const canProceed = useMemo(() => {
    if (step === 0) return !!selectedCat;
    if (step === 1) return photos.length > 0 && photos.every((p) => !p.uploading);
    if (step === 2) return !!loc?.lat;
    if (step === 3) return title.trim().length > 0;
    return true;
  }, [step, selectedCat, photos, loc, title]);

  const isIncident = selectedCat?.kind === "incident";
  const isEvent = selectedCat?.kind === "event";
  const isRoad = selectedCat?.kind === "road";

  // Default anonymous when the category has anonymous_default
  useEffect(() => {
    if (selectedCat) setAnonymous(!!selectedCat.anonymous_default);
  }, [selectedCat]);

  const submit = async () => {
    if (submitting || !selectedCat) return;
    setSubmitting(true);
    try {
      const payload = {
        category: selectedCat.slug,
        title: title.trim(),
        description: description.trim() || null,
        media: photos.filter((p) => p.url).map((p) => ({ url: p.url, kind: "image" })),
        date: dateStr, time: timeStr,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        location: {
          lat: loc.lat, lon: loc.lon, precision,
          display_name: loc.display_name, elevation_m: loc.elevation_m,
          country: loc.country, state: loc.state, county: loc.county,
          city: loc.city, village: loc.village, locality: loc.locality,
          country_id: loc.ids?.country, state_id: loc.ids?.state,
          county_id: loc.ids?.county, city_id: loc.ids?.city,
          village_id: loc.ids?.village, locality_id: loc.ids?.locality,
        },
        species: (commonName || scientificName) ? {
          common_name: commonName || null,
          scientific_name: scientificName || null,
        } : null,
        severity: severity || null,
        passable: passable || null,
        tags: tagsStr.split(",").map((t) => t.trim()).filter(Boolean),
        notes: notes.trim() || null,
        anonymous,
        ai_analysis: aiPayload || null,
        original_photo_timestamp: photoExif?.ts ? (typeof photoExif.ts === "string" ? photoExif.ts : new Date(photoExif.ts).toISOString()) : null,
      };
      await http.post("/nature/observations", payload);
      toast.success("Submitted for review. Moderators will publish it shortly.");
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const sheet = (
    <AnimatePresence>
      <motion.div
        key="bd"
        className="fixed inset-0 z-[80] bg-black/70"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        key="sheet"
        data-testid="add-obs-sheet"
        className="fixed z-[81] inset-x-0 bottom-0 md:inset-0 md:m-auto md:h-[92dvh] md:max-w-[720px] rounded-t-2xl md:rounded-2xl bg-[#F5EBDC] text-[#151312] flex flex-col overflow-hidden"
        initial={{ y: 400, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 400, opacity: 0 }}
        transition={{ type: "spring", stiffness: 220, damping: 26 }}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-3 border-b border-black/10 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              / New observation
            </div>
            <div className="font-serif-display text-xl leading-tight mt-0.5">
              Step {step + 1} of {STEPS.length} · {STEPS[step]}
            </div>
          </div>
          <button aria-label="Close" onClick={onClose} data-testid="add-obs-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-black/10">
          <div
            className="h-full bg-[color:var(--np-brand,#B85F2A)] transition-all"
            style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {selectedCat?.safety_note && step > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-300 text-amber-900 p-3 text-sm flex gap-2">
              <Warning size={18} weight="fill" className="text-amber-600 shrink-0" />
              <div>{selectedCat.safety_note}</div>
            </div>
          )}

          {/* -------- Step 1 — Category -------- */}
          {step === 0 && (
            <div>
              <input
                value={catQuery} onChange={(e) => setCatQuery(e.target.value)}
                placeholder="Search categories — rain, potholes, sunset, kingfisher…"
                data-testid="add-obs-cat-search"
                className="w-full bg-white border border-black/15 rounded-full px-4 py-2 text-sm mb-3"
              />
              <ul className="grid grid-cols-1 gap-1.5">
                {filteredLeaves.slice(0, 300).map((leaf) => (
                  <li key={leaf.slug}>
                    <button
                      onClick={() => setSelectedCat(leaf)}
                      data-testid={`add-obs-cat-${leaf.slug}`}
                      className={`w-full text-left rounded-lg px-3 py-2.5 border ${
                        selectedCat?.slug === leaf.slug
                          ? "bg-[#151312] text-[#F5EBDC] border-[#151312]"
                          : "bg-white/70 border-black/10 hover:border-[color:var(--np-brand,#B85F2A)]"
                      }`}
                    >
                      <div className="font-serif-display text-base leading-tight">
                        {leaf.emoji && <span className="mr-1.5">{leaf.emoji}</span>}
                        {leaf.label}
                      </div>
                      <div className={`font-mono-tech text-[9px] tracking-[0.22em] uppercase mt-1 ${
                        selectedCat?.slug === leaf.slug ? "text-[#E4A15C]" : "text-[color:var(--np-muted,#3A3830)]"
                      }`}>
                        {leaf.breadcrumb}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* -------- Step 2 — Photos -------- */}
          {step === 1 && (
            <div>
              <button
                onClick={pickPhotos}
                data-testid="add-obs-pick-photos"
                className="w-full rounded-xl border-2 border-dashed border-black/25 bg-white/60 p-6 flex flex-col items-center gap-2 hover:bg-white"
              >
                <Camera size={28} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
                <div className="font-serif-display text-lg">Take or pick a photo</div>
                <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]">JPG · PNG · WEBP · up to 6 photos</div>
              </button>
              <input
                ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp"
                onChange={onFiles} className="hidden"
                data-testid="add-obs-file-input"
              />
              {photos.length > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {photos.map((p, i) => (
                    <div key={i} className="relative rounded-lg overflow-hidden border border-black/10 bg-black/5">
                      <img src={absUrl(p.url) || p.previewUrl} alt="" className="w-full h-24 object-cover" />
                      {p.uploading && <div className="absolute inset-0 grid place-items-center bg-black/40 text-white font-mono-tech text-[9px] tracking-widest">Uploading…</div>}
                      <button
                        onClick={() => removePhoto(i)}
                        data-testid={`add-obs-remove-photo-${i}`}
                        className="absolute top-1 right-1 rounded-full bg-black/60 text-white p-1"
                        aria-label="Remove"
                      >
                        <X size={10} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* -------- Step 3 — Location -------- */}
          {step === 2 && (
            <div className="space-y-3">
              <button
                onClick={detectLocation}
                disabled={locBusy}
                data-testid="add-obs-detect-location"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
              >
                <MapPin size={14} weight="fill" /> {locBusy ? "Detecting…" : loc ? "Re-detect" : "Detect my location"}
              </button>
              {loc && (
                <div className="rounded-xl bg-white border border-black/10 p-4">
                  <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-1">Detected location</div>
                  <div className="font-serif-display text-lg leading-tight">{loc.display_name || "Unknown place"}</div>
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                    {["country", "state", "county", "city", "village", "locality"].map((k) => (
                      loc[k] ? (
                        <div key={k}>
                          <span className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)]">{k}</span>
                          <span className="ml-1.5">{loc[k]}</span>
                        </div>
                      ) : null
                    ))}
                    <div className="col-span-2 font-mono-tech text-[10px] tracking-[0.22em] text-[color:var(--np-muted,#3A3830)]">
                      {loc.lat?.toFixed(4)}, {loc.lon?.toFixed(4)}
                      {loc.elevation_m != null && <> · {Math.round(loc.elevation_m)} m</>}
                    </div>
                  </div>
                </div>
              )}

              {photoExif?.lat && loc?.lat && photoVsLocKm != null && photoVsLocKm > 0.5 && (
                <div className="rounded-xl bg-amber-50 border border-amber-300 text-amber-900 p-4" data-testid="location-conflict">
                  <div className="flex gap-2 items-start">
                    <Warning size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-serif-display text-base">Photo & device location differ by {photoVsLocKm.toFixed(photoVsLocKm < 10 ? 1 : 0)} km.</div>
                      <div className="text-sm mt-0.5">Which location should this observation use?</div>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      onClick={usePhotoLocation}
                      data-testid="use-photo-location"
                      className="rounded-lg border border-amber-500/60 bg-white/60 p-3 text-left"
                    >
                      <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-amber-700">Photo</div>
                      <div className="text-sm font-serif-display mt-0.5">{photoExif.lat.toFixed(4)}, {photoExif.lon.toFixed(4)}</div>
                    </button>
                    <button
                      onClick={detectLocation}
                      data-testid="use-device-location"
                      className="rounded-lg border border-amber-500/60 bg-white/60 p-3 text-left"
                    >
                      <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-amber-700">Current</div>
                      <div className="text-sm font-serif-display mt-0.5">{loc.lat.toFixed(4)}, {loc.lon.toFixed(4)}</div>
                    </button>
                  </div>
                </div>
              )}
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mb-1.5">Location precision</div>
                <div className="flex flex-wrap gap-1.5">
                  {["exact", "approximate", "city"].map((p) => (
                    <button
                      key={p}
                      onClick={() => setPrecision(p)}
                      data-testid={`add-obs-precision-${p}`}
                      className={`rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase border ${
                        precision === p ? "bg-[#151312] text-[#F5EBDC] border-[#151312]" : "border-black/20 hover:bg-white"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* -------- Step 4 — Details -------- */}
          {step === 3 && (
            <div className="space-y-3">
              {aiApplied && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-900 p-3 text-sm flex gap-2 items-start" data-testid="ai-suggested-banner">
                  <Sparkle size={16} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <b>AI Suggested</b> — title, description, name and tags were pre-filled from the photo{aiPayload?.confidence != null && <> ({Math.round(aiPayload.confidence * 100)}% confidence)</>}. Everything is editable — the observation carries an <b>AI Suggested</b> trust label.
                  </div>
                </div>
              )}
              {aiBusy && (
                <div className="rounded-lg border border-black/10 bg-white/70 p-3 text-sm flex gap-2" data-testid="ai-analyzing">
                  <Sparkle size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
                  Analysing photo…
                </div>
              )}
              <input
                value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Title — what did you observe?"
                data-testid="add-obs-title"
                className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-base"
              />
              <textarea
                value={description} onChange={(e) => setDescription(e.target.value)}
                rows={3} placeholder="Description (optional)"
                data-testid="add-obs-description"
                className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)}
                  data-testid="add-obs-date"
                  className="bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
                />
                <input
                  type="time" value={timeStr} onChange={(e) => setTimeStr(e.target.value)}
                  data-testid="add-obs-time"
                  className="bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {selectedCat?.kind === "nature" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input
                    value={commonName} onChange={(e) => setCommonName(e.target.value)}
                    placeholder="Common name" data-testid="add-obs-common"
                    className="bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
                  />
                  <input
                    value={scientificName} onChange={(e) => setScientificName(e.target.value)}
                    placeholder="Scientific name (if known)" data-testid="add-obs-scientific"
                    className="bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              )}

              {(isEvent || isIncident) && (
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mb-1.5">Severity</div>
                  <div className="flex flex-wrap gap-1.5">
                    {["minor", "moderate", "serious", "severe", "unknown"].map((s) => (
                      <button
                        key={s}
                        onClick={() => setSeverity(severity === s ? "" : s)}
                        data-testid={`add-obs-sev-${s}`}
                        className={`rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase border ${
                          severity === s ? "bg-[#151312] text-[#F5EBDC] border-[#151312]" : "border-black/20"
                        }`}
                      >{s}</button>
                    ))}
                  </div>
                </div>
              )}

              {isRoad && (
                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mb-1.5">Road passable?</div>
                  <div className="flex flex-wrap gap-1.5">
                    {["yes", "partially", "no", "unknown"].map((s) => (
                      <button
                        key={s}
                        onClick={() => setPassable(passable === s ? "" : s)}
                        data-testid={`add-obs-pass-${s}`}
                        className={`rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase border ${
                          passable === s ? "bg-[#151312] text-[#F5EBDC] border-[#151312]" : "border-black/20"
                        }`}
                      >{s}</button>
                    ))}
                  </div>
                </div>
              )}

              <input
                value={tagsStr} onChange={(e) => setTagsStr(e.target.value)}
                placeholder="Tags, comma, separated"
                data-testid="add-obs-tags"
                className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
              />
              <textarea
                value={notes} onChange={(e) => setNotes(e.target.value)}
                rows={2} placeholder="Notes (private context, source, etc.)"
                data-testid="add-obs-notes"
                className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          )}

          {/* -------- Step 5 — Review -------- */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="rounded-xl bg-white border border-black/10 p-4">
                <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-1">Summary</div>
                <div className="font-serif-display text-xl leading-tight">{title || "Untitled observation"}</div>
                <div className="mt-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]">
                  {selectedCat?.breadcrumb} · {dateStr} {timeStr}
                </div>
                {loc && <div className="mt-2 text-sm">{loc.display_name}</div>}
                {photos.length > 0 && (
                  <div className="mt-3 flex gap-1.5 overflow-x-auto">
                    {photos.map((p, i) => (
                      <img key={i} src={absUrl(p.url) || p.previewUrl} className="w-16 h-16 rounded object-cover" alt="" />
                    ))}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox" checked={anonymous}
                  onChange={(e) => setAnonymous(e.target.checked)}
                  data-testid="add-obs-anonymous"
                />
                Submit anonymously {selectedCat?.anonymous_default && <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)]">recommended</span>}
              </label>

              <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 p-3 text-sm flex gap-2">
                <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0" />
                <div>
                  Your submission will be labelled <b>User Observation</b> and enter the moderation queue. Once approved it will be published and appear on relevant geographic pages.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-black/10 flex items-center justify-between gap-3">
          <button
            onClick={() => (step > 0 ? setStep(step - 1) : onClose())}
            data-testid="add-obs-back"
            className="inline-flex items-center gap-1 rounded-full border border-black/20 px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase"
          >
            <ArrowLeft size={12} weight="bold" /> {step === 0 ? "Cancel" : "Back"}
          </button>
          {step < STEPS.length - 1 ? (
            <button
              disabled={!canProceed}
              onClick={() => setStep(step + 1)}
              data-testid="add-obs-next"
              className="inline-flex items-center gap-1 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-40"
            >
              Next <ArrowRight size={12} weight="bold" />
            </button>
          ) : (
            <button
              disabled={submitting || !canProceed}
              onClick={submit}
              data-testid="add-obs-submit"
              className="inline-flex items-center gap-1 rounded-full bg-[#E4A15C] text-[#151312] px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-40"
            >
              <CheckCircle size={12} weight="fill" /> {submitting ? "Submitting…" : "Submit for review"}
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(sheet, document.body);
}
